/**
 * Platform composition-root contracts (Electron Phase 1, PLAN §4 + AMENDMENT A3).
 *
 * This module is the vocabulary the rest of the app uses to ask *capability*
 * questions instead of *platform* questions. Nothing here imports Capacitor:
 * `runtime.ts` is the single detection site, and `capabilities.ts` is the single
 * place a behavioural decision is written down.
 *
 * AMENDMENT A3.2 — three different questions, three different predicates:
 *
 * | Question                         | Predicate            |
 * |----------------------------------|----------------------|
 * | Is this a packaged native install? | `runtime.isNative`   |
 * | Is this a phone or tablet?         | `runtime.isMobile`   |
 * | Can it do X?                       | `hasCapability('X')` |
 *
 * `Capacitor.isNativePlatform()` is literally `getPlatform() !== 'web'`, so the
 * Electron shell is "native". Any site that means "mobile" but tests "native"
 * is a correctness bug, not a style preference.
 */

/** Every runtime PointCraft POS can execute in. */
export type RuntimeKind = 'ios' | 'android' | 'web' | 'electron';

export const RUNTIME_KINDS: readonly RuntimeKind[] = ['ios', 'android', 'web', 'electron'];

export function isRuntimeKind(value: string): value is RuntimeKind {
  return (RUNTIME_KINDS as readonly string[]).includes(value);
}

export interface PosRuntime {
  readonly kind: RuntimeKind;
  /** A Capacitor bridge or the Electron shell is present (packaged install). */
  readonly isNative: boolean;
  /** iOS or Android only: OS permission prompts, Capgo OTA, mobile chrome. */
  readonly isMobile: boolean;
  /** The Electron desktop shell. */
  readonly isDesktopShell: boolean;
  readonly capabilities: RuntimeCapabilities;
}

/**
 * The capability table's row shape.
 *
 * Every field is a *behavioural* decision, never a platform label. Adding a
 * field means adding four explicit cells in `capabilities.ts` — there are no
 * computed defaults, so a reviewer reads the table rather than the code.
 */
export interface RuntimeCapabilities {
  // ---- identity & auth -------------------------------------------------
  /** Use `@capacitor-firebase/authentication` instead of the Firebase web SDK. */
  nativeFirebaseAuth: boolean;
  /** Which Firebase app `getFirebaseWebApp()` creates. */
  firebaseWebApp: 'default' | 'named-web-services';
  /** `getAuth()` vs `initializeAuth(app, { persistence: indexedDBLocalPersistence })`. */
  firebaseAuthInit: 'get-auth' | 'initialize-auth-indexeddb';
  /** The `BusinessDeviceType` this runtime registers as. */
  deviceRegistrationType: 'POS_DEVICE' | 'POS_WEB_DEVICE';
  /**
   * The login screen may offer the pairing shortcuts (Admin / Demo / Extra test
   * business, `login.tsx`). This is only the *runtime* half of the gate: the
   * environment half (`canRequestDemoAccess` — never in a production build) is
   * applied identically on every runtime, and the backend checks again.
   */
  loginPairingShortcuts: boolean;

  // ---- storage ---------------------------------------------------------
  /** Which secure-storage backend holds the device session and provider secrets. */
  secureStorage: 'keychain' | 'electron-safe-storage' | 'local-storage';
  /** Local database engine. `'sqlite-electron'` is reserved for Phase 2. */
  localDatabase: 'sqlite-native' | 'dexie';

  // ---- payments --------------------------------------------------------
  /** Card-not-present entry. `'none'` on electron: the action is Stripe-specific (A2). */
  manualCardEntry: 'payment-sheet' | 'elements' | 'none';
  /**
   * BlockChyp LOCAL_NETWORK (terminal in local/LAN mode) dispatch from this
   * device. iOS: the native plugin. Electron: the official SDK in the main
   * process (`payments-lan.electron.ts`). Device-owned CLOUD_RELAY and offline
   * LAN store-and-forward remain iOS-only in the V5 table.
   */
  blockchypLocalNetwork: boolean;
  /**
   * Admin/debug tools may build the official BlockChyp SDK client **in the
   * renderer** (admin BlockChyp console, terminal-access debug tab, payment
   * debug kit). `false` on electron: A1.3 — no signing client in the desktop
   * renderer; the desktop reaches BlockChyp only through `PlatformPayments`.
   */
  rendererBlockChypConsole: boolean;
  /** Pre-payment BlockChyp CLOUD availability probe runs from this runtime. */
  blockchypCloudProbe: boolean;
  /** Stripe M2 (BLE reader) sessions are available. */
  stripeM2: boolean;
  /** Terminal status may be resolved by a direct LAN ping (vs API/Firestore). */
  localTerminalPing: boolean;
  /** The `platform` tag written onto completed-sale reports. */
  completedReportPlatform: 'IOS' | 'ANDROID' | 'WEB' | 'DESKTOP';
  /**
   * T4-07: the operator can review and reconcile a card payment whose outcome
   * main's dispatch journal could not confirm (`pc:payments:reconcile-dispatch`).
   * Electron only — no other runtime keeps a main-process dispatch journal.
   * Open to every signed-in operator (owner decision), never manager-gated.
   */
  desktopDispatchReconcile: boolean;

  // ---- printing --------------------------------------------------------
  /** Which Star backend `starPrintService` drives. `'mock'` is web-only. */
  thermalPrinting: 'star-native' | 'star-electron-lan' | 'mock' | 'none';
  /** Bluetooth/BLE peripherals (radio state, BT printer interfaces). */
  bluetoothPeripherals: boolean;
  /** `window.print()` is a usable receipt fallback. */
  desktopPrintDialog: boolean;
  /**
   * Generic ESC/POS printers over the LAN, driven from the Electron main
   * process (`electron/main/services/escpos-lan`). Only the desktop shell has
   * a raw TCP transport; mobile keeps the Star SDK and web keeps the mock.
   */
  escPosLanPrinting: boolean;
  /**
   * Attached generic ESC/POS printers — installed Windows print queues (RAW
   * spooler jobs) and serial/COM ports — through the shell's OS print bridge.
   */
  escPosAttachedPrinting: boolean;

  /** Operational order audio backend; old mobile shells may use the web fallback. */
  orderAudio: 'native' | 'web';

  // ---- platform services ----------------------------------------------
  /** The OS prompts for camera/location/etc. and can deny them. */
  osPermissionPrompts: boolean;
  /** Capgo over-the-air bundle updates. */
  otaUpdates: boolean;
  /**
   * Where the running app version comes from (`resolveAppVersion`).
   * `capacitor-native`: Capacitor App + Capgo bundle (iOS/Android, unchanged).
   * `platform-shell`: `PlatformDevice.getShellInfo()` — Electron's
   * `app.getVersion()`, i.e. the installed package's version.
   * `build`: the compiled bundle's `VITE_APP_VERSION` (web, unchanged).
   */
  appVersionSource: 'capacitor-native' | 'platform-shell' | 'build';
  /** Remote minimum-version / update-policy enforcement. */
  remoteVersionPolicy: boolean;
  /** Device diagnostic log capture + upload. */
  deviceDiagnostics: boolean;
  /** Battery level/state telemetry. */
  batteryTelemetry: boolean;
  /** Wi-Fi management affordances. */
  wifiManagement: 'android' | 'ios-settings' | 'none';
  /** How an external URL is opened. */
  inAppBrowser: 'capacitor-browser' | 'window-open';
}

export type RuntimeCapabilityKey = keyof RuntimeCapabilities;
